import type { BlockDefinition, CountdownProps } from '../types';
import { escapeHtml } from '../html';

/**
 * Countdown — the no-major-competitor-ships-this differentiator (per the
 * plan). Two modes for the two real use cases:
 *
 *   1. 'duration' (default) — fake urgency timer. Each visitor sees a
 *      fresh countdown starting from `durationSeconds` on page load.
 *      The classic "only 2:30 left!" conversion lever. Persists per
 *      visitor only (in-memory) so the urgency stays believable for
 *      genuinely new arrivals from IG.
 *
 *   2. 'datetime' — real event countdown to a fixed wall-clock time.
 *      Use for actual launches, drop dates, deadlines.
 *
 * Renders as static HTML server-side plus a tiny inline vanilla-JS snippet
 * (~700 bytes) that ticks once per second. Multiple countdowns on one page
 * tick independently via per-block ids.
 */
export const countdown: BlockDefinition<CountdownProps> = {
  type: 'countdown',
  meta: {
    label: 'Countdown',
    icon: '⏳',
    category: 'content',
    description: 'Urgency timer or live countdown to a date.',
  },
  defaults: () => ({
    // Duration is the default because urgency-on-bio-link is by far the
    // more common creator use case. Event launches are the niche.
    mode: 'duration',
    label: 'Offer ends in',
    targetIso: new Date(Date.now() + 24 * 60 * 60 * 1000).toISOString(),
    durationSeconds: 5 * 60, // 5 minutes — the conversion-test sweet spot
    expiredText: "It's live!",
    align: 'center',
  }),
  validate(input) {
    const i = (input ?? {}) as Partial<CountdownProps>;

    let mode: CountdownProps['mode'] =
      i.mode === 'datetime' ? 'datetime' : i.mode === 'duration' ? 'duration' : null!;
    // Back-compat: legacy rows that only have `targetIso` and no `mode`
    // were created before this field existed — assume the datetime use case.
    if (!mode) {
      mode = i.targetIso && !i.durationSeconds ? 'datetime' : 'duration';
    }

    let targetIso = typeof i.targetIso === 'string' ? i.targetIso : '';
    if (!targetIso || Number.isNaN(Date.parse(targetIso))) {
      targetIso = new Date(Date.now() + 24 * 60 * 60 * 1000).toISOString();
    }

    let durationSeconds = typeof i.durationSeconds === 'number' ? Math.floor(i.durationSeconds) : 300;
    // Clamp: 1s to 7 days. Anything outside that is almost certainly a
    // misconfiguration (and a 7-day "urgency timer" isn't urgent anyway).
    if (!Number.isFinite(durationSeconds) || durationSeconds < 1) durationSeconds = 300;
    if (durationSeconds > 7 * 24 * 60 * 60) durationSeconds = 7 * 24 * 60 * 60;

    return {
      mode,
      label: typeof i.label === 'string' ? i.label.slice(0, 120) : '',
      targetIso,
      durationSeconds,
      expiredText: typeof i.expiredText === 'string' ? i.expiredText.slice(0, 120) : "It's live!",
      align: ['left', 'center', 'right'].includes(i.align as string)
        ? (i.align as CountdownProps['align'])
        : 'center',
    };
  },
  render(props) {
    const isDuration = props.mode === 'duration';
    const safeId = 'pb_cd_' + Math.random().toString(36).slice(2, 10);
    const justify =
      props.align === 'left' ? 'flex-start' : props.align === 'right' ? 'flex-end' : 'center';

    // Compute initial server-rendered numbers. For 'datetime' it's the
    // real time remaining; for 'duration' it's the full duration (the JS
    // snippet then resets `end` to now+duration on actual page load,
    // since the server render moment can be many ms before the visitor's
    // page hits the screen).
    const initialMs = isDuration
      ? props.durationSeconds * 1000
      : Math.max(0, new Date(props.targetIso).getTime() - Date.now());
    const expiredAtServer = !isDuration && initialMs <= 0;

    if (expiredAtServer) {
      return `<div class="pb-block pb-countdown" style="text-align:${props.align};">
  ${props.label ? `<div style="font-size:14px;color:var(--text);opacity:0.75;margin-bottom:6px;">${escapeHtml(props.label)}</div>` : ''}
  <div style="font-size:22px;font-weight:600;color:var(--primary);">${escapeHtml(props.expiredText || "It's live!")}</div>
</div>`;
    }

    const { days, hours, minutes, seconds } = breakdown(initialMs);

    // Choose which fields to display. For very short durations (<= 1 hour),
    // hide days. For ultra-short (<= 1 minute), hide hours/mins too.
    // Datetime mode always shows all four since the customer may want
    // a long countdown.
    const showDays = isDuration ? props.durationSeconds > 86400 : true;
    const showHours = isDuration ? props.durationSeconds > 3600 : true;
    const showMinutes = isDuration ? props.durationSeconds > 60 : true;

    const dataAttr = isDuration
      ? `data-pb-cd-duration="${props.durationSeconds}"`
      : `data-pb-cd="${new Date(props.targetIso).getTime()}"`;

    return `<div class="pb-block pb-countdown" ${dataAttr} data-pb-id="${safeId}" style="text-align:${props.align};">
  ${props.label ? `<div style="font-size:14px;color:var(--text);opacity:0.75;margin-bottom:8px;">${escapeHtml(props.label)}</div>` : ''}
  <div style="display:inline-flex;gap:8px;justify-content:${justify};font-variant-numeric:tabular-nums;">
    ${showDays ? unit(days, 'days') : ''}
    ${showHours ? unit(hours, 'hours') : ''}
    ${showMinutes ? unit(minutes, 'minutes') : ''}
    ${unit(seconds, 'seconds')}
  </div>
</div>
<script>(function(){var el=document.querySelector('[data-pb-id="${safeId}"]');if(!el)return;var dur=el.getAttribute('data-pb-cd-duration');var t=dur?Date.now()+Number(dur)*1000:Number(el.getAttribute('data-pb-cd'));function pad(n){return String(n).padStart(2,'0')}function set(u,v){var n=el.querySelector('[data-u='+u+'] [data-v]');if(n)n.textContent=pad(v);}function tick(){var ms=t-Date.now();if(ms<=0){el.innerHTML='<div style="font-size:22px;font-weight:600;color:var(--primary);">${escapeHtml(props.expiredText || "It's live!").replace(/'/g, "\\'")}</div>';return;}var s=Math.floor(ms/1000);var d=Math.floor(s/86400);var h=Math.floor(s%86400/3600);var m=Math.floor(s%3600/60);var sec=s%60;set('days',d);set('hours',h);set('minutes',m);set('seconds',sec);}tick();setInterval(tick,1000);})();</script>`;
  },
};

function breakdown(ms: number): { days: number; hours: number; minutes: number; seconds: number } {
  const s = Math.floor(ms / 1000);
  return {
    days: Math.floor(s / 86400),
    hours: Math.floor((s % 86400) / 3600),
    minutes: Math.floor((s % 3600) / 60),
    seconds: s % 60,
  };
}

function pad2(n: number): string {
  return String(n).padStart(2, '0');
}

function unit(n: number, key: 'days' | 'hours' | 'minutes' | 'seconds'): string {
  return `<div data-u="${key}" style="background:rgba(0,0,0,0.06);border-radius:var(--radius-card);padding:10px 12px;min-width:54px;">
    <div style="font-size:22px;font-weight:700;color:var(--text);line-height:1;"><span data-v>${pad2(n)}</span></div>
    <div style="font-size:10px;text-transform:uppercase;letter-spacing:0.05em;color:var(--text);opacity:0.55;margin-top:4px;">${key.slice(0, 3)}</div>
  </div>`;
}
